import React from 'react';

type Polarity = 'positive' | 'negative';

export type MatchRange = {
  start: number;
  end: number;
  text: string;
  polarity: Polarity;
};

/*
This file logic:
- Finds the nearest comparison number to the text
- Colors the comparison number green if it is positive, red if it is negative
- Does not color the actual/target values
*/

const POSITIVE_WORDS = ['above', 'ahead', 'over', 'exceed', 'exceeded', 'surpass', 'surpassing', 'surpassed', 'up', 'higher', 'increase', 'increased', 'growth', 'rise', 'rose', 'grow', 'grew'] as const;
const NEGATIVE_WORDS = ['below', 'behind', 'under', 'miss', 'missed', 'short', 'down', 'lower', 'fall', 'fell', 'decrease', 'decreases', 'decreased', 'drop', 'dropped', 'decline', 'declines', 'declined', 'reduce', 'reduces', 'reduced'] as const;

const COMPARISON_KEYWORDS = [
  'gap',
  'variance',
  'delta',
  'diff',
  'difference',
  'vs',
  'versus',
  'against',
  'target',
  'plan',
  'ap',
  'change',
  ...POSITIVE_WORDS,
  ...NEGATIVE_WORDS,
];

/** Collect all character positions where a polarity word (exceed, exceeded, etc.) starts. */
function getPolarityWordPositions(textLower: string): number[] {
  const positions: number[] = [];
  const words = [...POSITIVE_WORDS, ...NEGATIVE_WORDS];
  for (const w of words) {
    const re = new RegExp(`\\b${w}\\b`, 'ig');
    for (const m of textLower.matchAll(re)) {
      if (typeof m.index === 'number') positions.push(m.index);
    }
  }
  return positions;
}

// Candidate numbers with optional sign and optional unit.
// Use (\d+(?:,\d+)*) instead of (\d[\d,]*) to avoid swallowing trailing commas (e.g. "-4,").
const NUMBER_CANDIDATE_RE = /(?<!\w)([+\-−])?[ \t]*(\d+(?:,\d+)*(?:\.\d+)?)([ \t]*(?:%|pp|pts?|bps))?(?!\w)/g;

function findAllOccurrences(haystack: string, needle: string): number[] {
  if (!needle) return [];
  const out: number[] = [];
  let i = 0;
  while (true) {
    const idx = haystack.indexOf(needle, i);
    if (idx === -1) break;
    out.push(idx);
    i = idx + needle.length;
  }
  return out;
}

/**
 * Detects if a candidate number is likely a month/day segment of a date string
 * (e.g. the "-01" in "2025-01" or "2025 -01").
 */
function isDateSegment(text: string, start: number, end: number, sign: string | null, raw: string, hasUnit: boolean): boolean {
  if (hasUnit) return false; // Date segments like months/days don't have units like % or bps
  if (!sign || (sign !== '-' && sign !== '−')) return false;

  const before = text.slice(Math.max(0, start - 15), start);
  // Case 1: Preceded by 4-digit Year (e.g. "2025-01" or "2025 -01")
  if (/\b(19|20)\d{2}\s*$/.test(before)) {
    const numericPart = raw.replace(/^[+\-−]\s*/, '').trim();
    if (/^\d{1,2}$/.test(numericPart)) {
      const val = parseInt(numericPart, 10);
      return val >= 1 && val <= 31;
    }
  }
  // Case 2: Preceded by Year-Month (e.g. "2025-01-01")
  if (/\b(19|20)\d{2}[-−]\d{1,2}\s*$/.test(before)) {
    const numericPart = raw.replace(/^[+\-−]\s*/, '').trim();
    if (/^\d{1,2}$/.test(numericPart)) {
      const val = parseInt(numericPart, 10);
      return val >= 1 && val <= 31;
    }
  }
  return false;
}

/**
 * Detects if a candidate number is likely part of a numeric range (e.g. "400 - 500" or "(10-20)")
 * rather than a standalone negative number.
 */
function isRangeSegment(text: string, start: number, sign: string | null): boolean {
  if (!sign || (sign !== '-' && sign !== '−')) return false;

  const before = text.slice(Math.max(0, start - 20), start);

  // A hyphen is likely a range indicator if preceded by a standalone number (and optional space).
  // We use \b\d+ to ensure the digit is not part of an alphanumeric identifier (like MOFT1).
  if (/\b\d+\s*$/.test(before)) {
    // Exception: Years (19xx/20xx) followed by a sign are usually context for negative growth, not a range.
    if (/\b(19|20)\d{2}\s*$/.test(before)) {
      return false;
    }
    return true;
  }
  return false;
}

function containsComparisonKeyword(textLower: string): boolean {
  for (const kw of COMPARISON_KEYWORDS) {
    const re = new RegExp(`\\b${kw}\\b`, 'i');
    if (re.test(textLower)) return true;
  }
  return false;
}

function inferPolarity(textLower: string, range: { start: number; end: number }, sign?: string | null): Polarity | null {
  if (sign === '+') return 'positive';
  if (sign === '-' || sign === '−') return 'negative';

  // No sign → use nearby words (small window to avoid unrelated sentence context)
  const windowSize = 28;
  const from = Math.max(0, range.start - windowSize);
  const to = Math.min(textLower.length, range.end + windowSize);
  const around = textLower.slice(from, to);

  const hasPos = POSITIVE_WORDS.some((w) => new RegExp(`\\b${w}\\b`, 'i').test(around));
  const hasNeg = NEGATIVE_WORDS.some((w) => new RegExp(`\\b${w}\\b`, 'i').test(around));

  if (hasPos && !hasNeg) return 'positive';
  if (hasNeg && !hasPos) return 'negative';
  return null;
}

export function findComparisonNumbers(text: string): MatchRange[] {
  const textLower = text.toLowerCase();

  // Keyword positions
  const keywordPositions: number[] = [];
  for (const kw of COMPARISON_KEYWORDS) {
    const re = new RegExp(`\\b${kw}\\b`, 'ig');
    for (const m of textLower.matchAll(re)) {
      if (typeof m.index === 'number') keywordPositions.push(m.index);
    }
  }

  const polarityWordPositions = getPolarityWordPositions(textLower);

  const distanceToKeywords = (r: { start: number; end: number }) => {
    let best = Number.POSITIVE_INFINITY;
    for (const k of keywordPositions) {
      const d =
        k >= r.start && k <= r.end
          ? 0
          : Math.min(Math.abs(k - r.start), Math.abs(k - r.end));
      if (d < best) best = d;
    }
    return best;
  };

  const distanceToPolarityWords = (r: { start: number; end: number }) => {
    if (polarityWordPositions.length === 0) return Number.POSITIVE_INFINITY;
    let best = Number.POSITIVE_INFINITY;
    for (const p of polarityWordPositions) {
      const d =
        p >= r.start && p <= r.end
          ? 0
          : Math.min(Math.abs(p - r.start), Math.abs(p - r.end));
      if (d < best) best = d;
    }
    return best;
  };

  // Candidate number ranges (with sign/unit so we know if polarity is explicit)
  const candidates: Array<{
    start: number;
    end: number;
    raw: string;
    sign: string | null;
    hasUnit: boolean;
  }> = [];

  const POLARITY_WINDOW = 50; // max distance for "exceeded by 818" style

  for (const m of text.matchAll(NUMBER_CANDIDATE_RE)) {
    const idx = m.index;
    if (typeof idx !== 'number') continue;
    const sign = (m[1] ?? null) as string | null;
    const unit = (m[3] ?? '') as string;
    const raw = m[0];

    const leftTrim = raw.match(/^\s*/)?.[0]?.length ?? 0;
    const rightTrim = raw.match(/\s*$/)?.[0]?.length ?? 0;
    const start = idx + leftTrim;
    const end = idx + raw.length - rightTrim;
    const hasUnit = Boolean(unit && unit.trim().length > 0);
    const trimmedRaw = raw.slice(leftTrim, raw.length - rightTrim);

    // Skip segments that look like dates or ranges to avoid misidentifying them as negative numbers
    if (isDateSegment(text, start, end, sign, trimmedRaw, hasUnit) ||
      isRangeSegment(text, start, sign)) {
      continue;
    }

    // Require sign OR unit to avoid coloring every number (e.g. years, raw counts)
    // EXCEPT: allow unsigned unitless numbers when they're near a polarity word AND
    // look like a delta (e.g. "exceeded the target by **818**" or "**818** above target")
    // so we don't color the actual/target values (29,258 / 28,440).
    if (!sign && !hasUnit) {
      const distToPolarity = distanceToPolarityWords({ start, end });
      if (distToPolarity > POLARITY_WINDOW) continue;
      const before = text.slice(Math.max(0, start - 40), start);
      const after = text.slice(end, Math.min(text.length, end + 20));
      const afterWords = ['more', 'less', ...POSITIVE_WORDS, ...NEGATIVE_WORDS].join('|');
      const looksLikeDelta =
        /\b(by|of)\s*\*{0,2}\s*$/i.test(before) ||
        new RegExp(`^\\s*\\*{0,2}\\s*(${afterWords})\\b`, 'i').test(after);
      if (!looksLikeDelta) continue;
    }

    candidates.push({
      start,
      end,
      raw: trimmedRaw,
      sign,
      hasUnit,
    });
  }

  if (candidates.length === 0) return [];

  const MAX_DISTANCE = 500; // Increased to cover entire list sentences like "Top Growth: A (+10%), B (+20%), C (+30%)"

  // For explicitly signed numbers (+/-), no keyword proximity is required.
  // For unit numbers without sign: pick nearest to comparison keyword.
  // For unsigned unitless (e.g. "818" near "exceeded"): pick nearest to polarity word
  // so we color the delta, not the "28,440" target.
  const effectiveDistance = (c: { start: number; end: number; sign: string | null; hasUnit: boolean }) => {
    if (c.sign) return 0;
    const distKw = distanceToKeywords(c);
    const distPol = distanceToPolarityWords(c);
    if (c.hasUnit) return distKw;
    return distPol;
  };

  const matches: MatchRange[] = [];
  for (const best of candidates) {
    const dist = effectiveDistance(best);
    if (dist <= MAX_DISTANCE) {
      const polarity = inferPolarity(textLower, { start: best.start, end: best.end }, best.sign);
      if (polarity) {
        matches.push({ start: best.start, end: best.end, text: best.raw, polarity });
      }
    }
  }

  return matches;
}

export function extractPlainText(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractPlainText).join('');
  if (React.isValidElement(node)) return extractPlainText((node.props as any)?.children);
  return '';
}

export function colorizeReactChildrenByRange(
  children: React.ReactNode,
  range: { start: number; end: number },
  className: string
): React.ReactNode {
  const state = { pos: 0, key: 0 };

  const walk = (n: React.ReactNode): React.ReactNode => {
    if (n === null || n === undefined || typeof n === 'boolean') return n;

    if (typeof n === 'string' || typeof n === 'number') {
      const text = String(n);
      const start = state.pos;
      const end = state.pos + text.length;
      state.pos = end;

      // no overlap
      if (end <= range.start || start >= range.end) return n;

      const a = Math.max(0, range.start - start);
      const b = Math.min(text.length, range.end - start);

      const before = text.slice(0, a);
      const mid = text.slice(a, b);
      const after = text.slice(b);

      const out: React.ReactNode[] = [];
      if (before) out.push(before);
      if (mid) {
        const key = `cmp_${state.key++}`;
        out.push(
          <span key={key} className={className} data-cmp-colored={true}>
            {mid}
          </span>
        );
      }
      if (after) out.push(after);

      return out.length === 1 ? out[0] : out;
    }

    if (Array.isArray(n)) {
      const mapped = n.map((c) => walk(c));
      return mapped;
    }

    if (React.isValidElement(n)) {
      const props: any = n.props || {};
      if (props['data-cmp-colored']) {
        state.pos += extractPlainText(n).length;
        return n;
      }

      if (!props.children) return n;
      const newChildren = walk(props.children);
      return React.cloneElement(n as any, { ...props, children: newChildren });
    }

    return n;
  };

  return walk(children);
}

